import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStore } from 'react-redux';
import apiClient from '../../services/api';
import { initializeAuthSuccess } from '../../store/slices/authSlice';
import LanguageSelector from '../LanguageSelector';
import WelcomeScreen from './WelcomeScreen';
import VoiceRecorder from './VoiceRecorder';
import ToneArchetypeStep from './ToneArchetypeStep';
import ConsentForm from './ConsentForm';
import ContractPage from './ContractPage';
import SuccessScreen from './SuccessScreen';
import './OnboardingWizard.css';

function OnboardingWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // ``useStore`` returns the Redux store object itself (stable, no
  // re-render trigger). We read the current ``state.auth.user`` lazily
  // inside the fire-and-forget refresh below to compare with the
  // freshly fetched ``/auth/me`` payload — only dispatching if a
  // tracked field actually changed. Without this guard, EVERY
  // ``fetchOnboardingStatus`` call fired a Redux dispatch with a
  // brand-new ``user`` object (different reference, same data), which
  // every effect in the tree using ``user`` as a dep treated as a
  // change → cascade of re-renders + duplicate API calls.
  const store = useStore();
  const [currentStep, setCurrentStep] = useState('welcome');
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The ``t`` function reference from ``useTranslation`` flips a few
  // times during initial app load (i18next async locale fetch + auto
  // language detection). If we add ``t`` to the useCallback deps, the
  // status-fetch closure is recreated on every flip and the
  // ``useEffect([fetchOnboardingStatus])`` below re-fires the call,
  // racing with the previous one and (with the wrong ordering) leaving
  // the wizard stuck on ``loading=true``. ``t`` is only used here for
  // an error message — a slightly stale closure is harmless because
  // i18next resolves keys at call time, not at closure creation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOnboardingStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/onboarding/status');
      const status = response.data;
      setOnboardingStatus(status);

      // Set current step based on status
      // Never redirect away - let user see their current step even if complete
      if (status.current_step === 'complete' && status.onboarding_completed) {
        // Show success screen if truly complete (user can still see it)
        setCurrentStep('success');
      } else if (status.current_step && status.current_step !== 'complete') {
        // Set to the step returned by backend
        setCurrentStep(status.current_step);
      } else {
        // Fallback to welcome if step is unclear or not started
        setCurrentStep('welcome');
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
      setError(err.response?.data?.detail || t('onboarding.errors.load_status_failed'));
    } finally {
      // Settle the loading state BEFORE the optional Redux refresh below.
      // A hang on /auth/me must never trap the wizard in an infinite
      // spinner — the onboarding step can render with the previous
      // Redux user; we sync in the background.
      setLoading(false);
    }

    // Keep Redux user in sync — fields like tone_archetype change as
    // the user advances through onboarding, and stale Redux state
    // would otherwise cause the dashboard tone modal to appear after
    // exit. Fire-and-forget: we don't await the result, so any
    // failure or slowness here does not block the wizard rendering.
    //
    // CRITICAL: only dispatch when a tracked field is actually
    // different from the current Redux value. Dispatching a fresh
    // ``user`` object on every status fetch (even when the data is
    // identical) flips the Redux reference and refires every
    // ``useEffect([..., user, ...])`` in the app tree, producing a
    // cascade that looks like an infinite loop in the network tab.
    apiClient
      .get('/api/v1/auth/me')
      .then((meResponse) => {
        const fresh = meResponse?.data;
        if (!fresh) return;
        const current = store.getState().auth?.user;
        const trackedFields = [
          'id',
          'email',
          'role',
          'is_superuser',
          'tone_archetype',
          'voice_file_path',
          'materials_consent',
          'meta_accounts_consent',
          'contract_accepted',
          'onboarding_completed',
          'preferred_language',
          'feature_flags',
        ];
        const changed = !current || trackedFields.some((k) => {
          // Shallow compare; for ``feature_flags`` (object) compare
          // serialized form to catch nested toggles without pulling in
          // a deep-equal lib.
          if (k === 'feature_flags') {
            return JSON.stringify(current[k] || {}) !== JSON.stringify(fresh[k] || {});
          }
          return current[k] !== fresh[k];
        });
        if (changed) {
          dispatch(initializeAuthSuccess({ user: fresh }));
        }
      })
      .catch((refreshErr) => {
        console.error('Failed to refresh user after onboarding step:', refreshErr);
      });
  }, [dispatch, store]);

  useEffect(() => {
    fetchOnboardingStatus();
  }, [fetchOnboardingStatus]);

  const handleStepComplete = async () => {
    // Refresh status after each step
    await fetchOnboardingStatus();
  };

  const handleStart = () => {
    setCurrentStep('voice');
  };

  if (loading) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-loading">
          <div className="spinner"></div>
          <p>{t('onboarding.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="onboarding-container">
        <div className="onboarding-error">
          <p>{error}</p>
          <button onClick={fetchOnboardingStatus} className="retry-button">
            {t('onboarding.common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const steps = ['voice', 'tone', 'consent', 'contract'];
  const currentStepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;

  return (
    <div className="onboarding-container">
      <div className="onboarding-wizard">
        <div className="onboarding-language-selector">
          <LanguageSelector />
        </div>
        {currentStep !== 'welcome' && currentStep !== 'success' && (
          <div className="onboarding-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
              ></div>
            </div>
            <div className="progress-steps">
              {steps.map((step, index) => (
                <div 
                  key={step} 
                  className={`progress-step ${index <= currentStepIndex ? 'active' : ''} ${index < currentStepIndex ? 'completed' : ''}`}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-label">{t(`onboarding.welcome.steps.${step}`)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="onboarding-content">
          {currentStep === 'welcome' && (
            <WelcomeScreen onStart={handleStart} />
          )}
          {currentStep === 'voice' && (
            <VoiceRecorder 
              onComplete={handleStepComplete}
              onNext={() => setCurrentStep('tone')}
            />
          )}
          {currentStep === 'tone' && (
            <ToneArchetypeStep
              onComplete={handleStepComplete}
              onNext={() => setCurrentStep('consent')}
            />
          )}
          {currentStep === 'consent' && (
            <ConsentForm 
              onComplete={handleStepComplete}
              onNext={() => setCurrentStep('contract')}
            />
          )}
          {currentStep === 'contract' && (
            <ContractPage 
              onComplete={handleStepComplete}
              onNext={() => setCurrentStep('success')}
            />
          )}
          {currentStep === 'success' && (
            <SuccessScreen />
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;

